import apiService from './api';
import {
  transformFromAPI,
  transformInventoryItemForAPI,
  transformInventoryItemFromAPI,
  transformInventoryUsageForAPI
} from '../utils/dataTransform';
import { getErrorMessage } from '../utils';

class InventoryService {
  constructor() {
    this.endpoint = '/inventory-items';
  }

  async getInventoryItems(params = {}) {
    try {
      // Convert frontend pagination params to backend format
      const apiParams = {
        limit: params.limit || 25,
        offset: params.offset || 0,
        ...params
      };
      
      // Remove frontend-specific pagination params
      delete apiParams.page;
      delete apiParams.per_page;

      const response = await apiService.get(this.endpoint, apiParams);
      
      if (response.success) {
        // Transform snake_case response to camelCase for frontend
        const transformedData = {
          ...response,
          data: response.data ? response.data.map(item => transformInventoryItemFromAPI(item)) : []
        };
        return transformedData;
      } else {
        throw new Error(response.message || 'Failed to fetch inventory items');
      }
    } catch (error) {
      console.error('Error fetching inventory items:', error);
      throw error;
    }
  }

  async getInventoryItemById(id) {
    try {
      const response = await apiService.get(`${this.endpoint}/${id}`);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? transformInventoryItemFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to fetch inventory item');
      }
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      throw error;
    }
  }

  async createInventoryItem(itemData) {
    try {
      // Transform camelCase form data to snake_case for API
      const apiData = transformInventoryItemForAPI(itemData);
      
      const response = await apiService.post(this.endpoint, apiData);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? transformInventoryItemFromAPI(response.data) : null
        };
      } else {
        throw new Error(getErrorMessage(response.message || 'Failed to create inventory item', 'Failed to create inventory item'));
      }
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw new Error(getErrorMessage(error, 'Failed to create inventory item'));
    }
  }

  async updateInventoryItem(id, itemData) {
    try {
      // Transform camelCase form data to snake_case for API
      const apiData = transformInventoryItemForAPI(itemData);
      
      const response = await apiService.put(`${this.endpoint}/${id}`, apiData);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? transformInventoryItemFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to update inventory item');
      }
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  }

  async deleteInventoryItem(id) {
    try {
      const response = await apiService.delete(`${this.endpoint}/${id}`);
      
      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Failed to delete inventory item');
      }
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  }

  async recordInventoryUsage(id, usageData) {
    try {
      // Transform usage data to snake_case for API
      const apiData = transformInventoryUsageForAPI(usageData);
      
      const response = await apiService.post(`${this.endpoint}/${id}/usage`, apiData);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? transformFromAPI(response.data) : null
        };
      } else {
        throw new Error(response.message || 'Failed to record inventory usage');
      }
    } catch (error) {
      console.error('Error recording inventory usage:', error);
      throw error;
    }
  }

  async getInventoryMovements(id) {
    try {
      const response = await apiService.get(`${this.endpoint}/${id}/movements`);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? response.data.map(movement => transformFromAPI(movement)) : []
        };
      } else {
        throw new Error(response.message || 'Failed to fetch inventory movements');
      }
    } catch (error) {
      console.error('Error fetching inventory movements:', error);
      throw error;
    }
  }

  // Legacy method - use getInventoryMovements instead
  async getMovementHistory(id) {
    return this.getInventoryMovements(id);
  }

  async getLinkedPOs(id) {
    try {
      const response = await apiService.get(`${this.endpoint}/${id}/purchase-orders`);
      
      if (response.success) {
        return {
          ...response,
          data: response.data ? response.data.map(po => transformFromAPI(po)) : []
        };
      } else {
        throw new Error(response.message || 'Failed to fetch linked purchase orders');
      }
    } catch (error) {
      console.error('Error fetching linked POs:', error);
      throw error;
    }
  }

}

export const inventoryService = new InventoryService();
